// Módulo Arquivos — arquivamento AUTOMÁTICO (01/10/2026, Walber).
// Usado quando uma ação de fechamento dá certo (fechar a semana do Boletim de Ponto, fechar a área).
// Regras do Walber:
//  - o fechamento NUNCA depende do arquivo: se o arquivo falhar, o fechamento continua valendo;
//  - deu certo  → aviso verde "Arquivado com sucesso" com o botão Abrir;
//  - deu errado → aviso vermelho "<o que fechou>, mas o arquivo não foi guardado" com "Tentar de novo"
//    (o aviso de erro não some sozinho).
// Cada documento é tentado separadamente: se 1 de 4 falhar, os outros 3 ficam guardados e só o que
// falhou é refeito no "Tentar de novo".
import React, { useCallback, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useAuth } from './AuthContext';
import { mensagemDeErro } from './arquivosApi';
import { PedidoArquivo, abrirNoArquivos, arquivarHtmlPronto } from './arquivarCopia';

export interface TarefaArquivo {
  pedido: PedidoArquivo;
  /** Devolve a página HTML pronta (cópia fiel ou documento já montado). */
  html: () => Promise<string> | string;
}

interface Aviso {
  tom: 'success' | 'error';
  texto: string;
  pasta?: string;
  refazer?: TarefaArquivo[];
}

/** rotuloFalha: o que já fechou, para a mensagem de erro (ex.: "A área fechou", "O ponto da semana fechou"). */
export function useArquivoAutomatico(rotuloFalha: string) {
  const { token } = useAuth();
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [arquivando, setArquivando] = useState(false);

  const arquivar = useCallback(async (tarefas: TarefaArquivo[]) => {
    if (!tarefas.length) return;
    setArquivando(true);
    setAviso(null);
    const falhas: TarefaArquivo[] = [];
    const erros: string[] = [];
    let guardados = 0;
    let pasta: string | undefined = tarefas[0].pedido.pasta;
    for (const t of tarefas) {
      try {
        const html = await t.html();
        const novo = await arquivarHtmlPronto(token, html, t.pedido);
        guardados += 1;
        pasta = novo.pasta;
      } catch (e) {
        falhas.push(t);
        erros.push(`${t.pedido.titulo}: ${mensagemDeErro(e)}`);
      }
    }
    setArquivando(false);
    if (!falhas.length) {
      setAviso({
        tom: 'success',
        texto: guardados === 1 ? `Arquivado com sucesso · ${tarefas[0].pedido.titulo}` : `Arquivado com sucesso · ${guardados} documentos`,
        pasta,
      });
    } else {
      const parte = falhas.length === tarefas.length
        ? (tarefas.length === 1 ? 'o arquivo não foi guardado' : 'os arquivos não foram guardados')
        : `${falhas.length} de ${tarefas.length} documentos não foram guardados (os outros ${guardados} foram)`;
      setAviso({ tom: 'error', texto: `${rotuloFalha}, mas ${parte}. ${erros.join(' · ')}`, pasta, refazer: falhas });
    }
  }, [token, rotuloFalha]);

  const avisoNaTela = (
    <Snackbar className="noprint no-print" open={!!aviso || arquivando}
      autoHideDuration={aviso?.tom === 'success' ? 8000 : null}
      onClose={(_, motivo) => { if (motivo !== 'clickaway' && !arquivando) setAviso(null); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      {arquivando ? (
        <Alert severity="info" variant="filled">Arquivando no módulo Arquivos…</Alert>
      ) : aviso ? (
        <Alert severity={aviso.tom} variant="filled" onClose={() => setAviso(null)} sx={{ maxWidth: 560 }}
          action={aviso.tom === 'error' && aviso.refazer ? (
            <Button color="inherit" size="small" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              onClick={() => { const r = aviso.refazer as TarefaArquivo[]; void arquivar(r); }}>Tentar de novo</Button>
          ) : aviso.pasta ? (
            <Button color="inherit" size="small" sx={{ fontWeight: 700 }}
              onClick={() => abrirNoArquivos(aviso.pasta as string)}>Abrir</Button>
          ) : undefined}>
          {aviso.texto}
        </Alert>
      ) : undefined}
    </Snackbar>
  );

  return { arquivar, arquivando, avisoNaTela };
}
